import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { BellRing, Mail, MessageSquare } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

export default function AlertsSettings() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [testingKey, setTestingKey] = useState("");
  const [customTo, setCustomTo] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/alert-settings");
      setAlerts(data.alerts);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (a) => {
    const next = !a.enabled;
    setSavingKey(a.key);
    setAlerts((list) => list.map((x) => (x.key === a.key ? { ...x, enabled: next } : x)));
    try {
      await api.put("/alert-settings", { settings: { [a.key]: next } });
      toast.success(next ? `${a.label} enabled` : `${a.label} disabled`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setAlerts((list) => list.map((x) => (x.key === a.key ? { ...x, enabled: a.enabled } : x)));
    } finally { setSavingKey(""); }
  };

  const sendTest = async (a, channel) => {
    setTestingKey(`${a.key}:${channel}`);
    try {
      const { data } = await api.post("/alert-settings/test", { key: a.key, channel, to: customTo.trim() });
      toast.success(`Test ${channel === "sms" ? "SMS" : "email"} sent to ${data.to}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTestingKey(""); }
  };

  if (loading) return <div className={`${CARD} p-8 text-center max-w-4xl mx-auto`} style={{ color: "var(--sj-text-3)" }}>Loading alerts…</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="alerts-settings-page">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight flex items-center gap-2"><BellRing className="h-7 w-7" style={{ color: "var(--sj-primary)" }} /> Alerts</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Turn automatic alerts and reminders on or off for the whole church.</p>
      </div>
      <div className={`${CARD} p-4 flex flex-wrap items-center gap-3`}>
        <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Send test previews to</label>
        <input data-testid="alert-test-to-input" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
          placeholder="Your own account (or type an email / phone)"
          className="flex-1 min-w-[220px] bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20" />
        <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>Leave blank to send to yourself. Type an email for the Email button, or a phone number for SMS.</span>
      </div>
      <div className={`${CARD} divide-y`} style={{ borderColor: "var(--sj-border)" }}>
        {alerts.map((a) => (
          <div key={a.key} className="flex items-center justify-between gap-4 p-5" data-testid={`alert-row-${a.key}`}>
            <div className="min-w-0">
              <div className="font-semibold" style={{ color: "var(--sj-text)" }}>{a.label}</div>
              <div className="text-sm mt-0.5" style={{ color: "var(--sj-text-2)" }}>{a.description}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center rounded-full border overflow-hidden" style={{ borderColor: "var(--sj-border)" }}>
                <span className="text-[10px] uppercase font-bold px-2 py-1.5" style={{ color: "var(--sj-text-3)" }}>Test</span>
                <button
                  type="button"
                  onClick={() => sendTest(a, "email")}
                  disabled={testingKey.startsWith(`${a.key}:`)}
                  data-testid={`alert-test-${a.key}`}
                  title="Send me a test email"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 hover:bg-[#EDF2F4] disabled:opacity-60 border-l"
                  style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}
                >
                  <Mail className="h-3.5 w-3.5" /> {testingKey === `${a.key}:email` ? "…" : "Email"}
                </button>
                <button
                  type="button"
                  onClick={() => sendTest(a, "sms")}
                  disabled={testingKey.startsWith(`${a.key}:`)}
                  data-testid={`alert-test-sms-${a.key}`}
                  title="Send me a test SMS"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 hover:bg-[#EDF2F4] disabled:opacity-60 border-l"
                  style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}
                >
                  <MessageSquare className="h-3.5 w-3.5" /> {testingKey === `${a.key}:sms` ? "…" : "SMS"}
                </button>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={a.enabled}
                disabled={savingKey === a.key}
                data-testid={`alert-toggle-${a.key}`}
                onClick={() => toggle(a)}
                className="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60"
                style={{ background: a.enabled ? "var(--sj-primary)" : "#C7CDD1" }}
              >
                <span className="inline-block h-6 w-6 transform rounded-full bg-white transition-transform" style={{ transform: a.enabled ? "translateX(22px)" : "translateX(2px)" }} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
